import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface Step {
  key: string;
  label: string;
}

export interface StepperProps {
  steps: Step[];
  /** Zero-based index of the step being edited. */
  currentStep: number;
  /** Lets the user jump back to a step they have already completed. */
  onStepClick?: (index: number) => void;
  className?: string;
}

/**
 * One paint job, whatever the rail is drawn on.
 *
 * The platform console's banner used to need a second set of colours because it
 * was near-black; it is drawn on the same tinted sheet as every other console
 * now, so the accent and ink tokens read on all of them.
 */
const SKIN = {
  pill: 'hover:bg-surface-2',
  pillCurrent: 'bg-surface ring-1 ring-inset ring-accent-line',
  dotComplete: 'bg-accent text-white',
  dotCurrent: 'bg-accent-soft text-accent ring-2 ring-accent',
  dotIdle: 'bg-surface-2 text-ink-3',
  labelCurrent: 'font-[650] text-ink',
  labelIdle: 'text-ink-3',
  railDone: 'bg-accent',
  railIdle: 'bg-line',
} as const;

/**
 * Progress rail for multi-step forms.
 *
 * Completed steps are clickable, upcoming ones are not — jumping ahead past
 * unvalidated fields is how half-filled records get created.
 */
export function Stepper({ steps, currentStep, onStepClick, className }: StepperProps) {
  return (
    <ol className={cn('flex flex-wrap items-center gap-x-2 gap-y-3', className)}>
      {steps.map((step, index) => {
        const isComplete = index < currentStep;
        const isCurrent = index === currentStep;
        const canNavigate = isComplete && Boolean(onStepClick);

        return (
          <li key={step.key} className="flex items-center gap-2">
            <button
              type="button"
              disabled={!canNavigate}
              onClick={canNavigate ? () => onStepClick?.(index) : undefined}
              aria-current={isCurrent ? 'step' : undefined}
              className={cn(
                'flex items-center gap-2 rounded-pill px-2.5 py-1.5 transition-colors',
                isCurrent && SKIN.pillCurrent,
                canNavigate && SKIN.pill,
                !canNavigate && 'cursor-default',
              )}
            >
              <span
                className={cn(
                  'grid h-6 w-6 shrink-0 place-items-center rounded-pill font-mono text-2xs font-semibold transition-colors',
                  isComplete && SKIN.dotComplete,
                  isCurrent && SKIN.dotCurrent,
                  !isComplete && !isCurrent && SKIN.dotIdle,
                )}
                aria-hidden
              >
                {isComplete ? <Check className="h-3.5 w-3.5" strokeWidth={2.5} /> : index + 1}
              </span>
              <span
                className={cn(
                  'whitespace-nowrap text-sm',
                  isCurrent ? SKIN.labelCurrent : SKIN.labelIdle,
                )}
              >
                {step.label}
              </span>
            </button>

            {index < steps.length - 1 && (
              // The rail fills in behind the user, so how far along they are
              // reads at a glance rather than from counting ticks.
              <span
                className={cn(
                  'hidden h-0.5 w-8 rounded-pill transition-colors sm:block',
                  isComplete ? SKIN.railDone : SKIN.railIdle,
                )}
                aria-hidden
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}
